import { useEffect, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react'

export type GridItem = { i: string; x: number; y: number; w: number; h: number }

type DragHandle = { onPointerDown: (e: ReactPointerEvent) => void }

/**
 * 의존성 없는 드래그·리사이즈 그리드(React 19 안전 — findDOMNode 미사용).
 * 12열 격자에 카드를 절대 배치하고, 헤더 드래그로 이동·우하단 핸들로 리사이즈한다.
 */
export function GridCanvas<T extends GridItem>({
    items,
    onChange,
    editable,
    renderItem,
    cols = 12,
    rowHeight = 40,
    gap = 12,
    minSizeFor = () => ({ minW: 2, minH: 2 }),
}: {
    items: T[]
    onChange: (items: T[]) => void
    editable: boolean
    renderItem: (item: T, drag: DragHandle) => ReactNode
    cols?: number
    rowHeight?: number
    gap?: number
    // 카드 타입별 리사이즈 최소 크기(격자 칸). 이 아래로는 줄일 수 없다(내용 겹침 방지).
    minSizeFor?: (item: T) => { minW: number; minH: number }
}) {
    const ref = useRef<HTMLDivElement>(null)
    const [width, setWidth] = useState(1200)
    const [draft, setDraft] = useState<T[]>(items)
    // 최소 크기 함수는 매 렌더 새 참조일 수 있어 ref로 읽는다(이펙트 재구독 방지).
    const minSizeRef = useRef(minSizeFor)
    minSizeRef.current = minSizeFor
    const opRef = useRef<{
        id: string
        mode: 'move' | 'resize'
        px: number
        py: number
        orig: GridItem
    } | null>(null)

    useEffect(() => {
        setDraft(items)
    }, [items])

    useEffect(() => {
        const el = ref.current
        if (!el) return
        const ro = new ResizeObserver((entries) => {
            const w = entries[0]?.contentRect.width
            if (w) setWidth(w)
        })
        ro.observe(el)
        return () => ro.disconnect()
    }, [])

    // 열 너비는 컨테이너 폭에 유동적으로 맞춘다(AI 빌더 패널이 열리면 보드가 자연히 줄어든다).
    const colWidth = width / cols

    useEffect(() => {
        if (!editable) return
        const onMove = (e: globalThis.PointerEvent) => {
            const op = opRef.current
            if (!op) return
            const dCol = Math.round((e.clientX - op.px) / colWidth)
            const dRow = Math.round((e.clientY - op.py) / rowHeight)
            setDraft((prev) =>
                prev.map((it) => {
                    if (it.i !== op.id) return it
                    if (op.mode === 'move') {
                        return {
                            ...it,
                            x: Math.max(
                                0,
                                Math.min(cols - op.orig.w, op.orig.x + dCol)
                            ),
                            y: Math.max(0, op.orig.y + dRow),
                        }
                    }
                    // 리사이즈: 타입별 최소 크기 아래로는 줄어들지 않게 하한을 건다.
                    const { minW, minH } = minSizeRef.current(it)
                    return {
                        ...it,
                        w: Math.max(
                            minW,
                            Math.min(cols - op.orig.x, op.orig.w + dCol)
                        ),
                        h: Math.max(minH, op.orig.h + dRow),
                    }
                })
            )
        }
        const onUp = () => {
            if (!opRef.current) return
            opRef.current = null
            setDraft((prev) => {
                onChange(prev)
                return prev
            })
        }
        window.addEventListener('pointermove', onMove)
        window.addEventListener('pointerup', onUp)
        return () => {
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('pointerup', onUp)
        }
    }, [editable, colWidth, cols, rowHeight, onChange])

    const start = (
        e: ReactPointerEvent,
        id: string,
        mode: 'move' | 'resize'
    ) => {
        if (!editable) return
        e.preventDefault()
        const it = draft.find((d) => d.i === id)
        if (!it) return
        opRef.current = {
            id,
            mode,
            px: e.clientX,
            py: e.clientY,
            orig: { x: it.x, y: it.y, w: it.w, h: it.h, i: it.i },
        }
    }

    const rows = Math.max(8, ...draft.map((d) => d.y + d.h), 8)

    return (
        <div
            ref={ref}
            className='relative w-full'
            style={{ height: rows * rowHeight }}>
            {draft.map((it) => (
                <div
                    key={it.i}
                    className='absolute'
                    style={{
                        left: it.x * colWidth,
                        top: it.y * rowHeight,
                        width: Math.max(0, it.w * colWidth - gap),
                        height: Math.max(0, it.h * rowHeight - gap),
                    }}>
                    {renderItem(it, {
                        onPointerDown: (e) => start(e, it.i, 'move'),
                    })}
                    {editable ? (
                        <div
                            onPointerDown={(e) => start(e, it.i, 'resize')}
                            title='크기 조절'
                            className='border-primary/60 absolute right-0.5 bottom-0.5 size-3 cursor-se-resize rounded-sm border-r-2 border-b-2'
                        />
                    ) : null}
                </div>
            ))}
        </div>
    )
}
